import { IconClose } from "@/assets/icons/geist/IconClose";
import {
    WorkflowEditFormTriggerContextType,
    type WorkflowEditFormTriggerContext,
} from "@/components/automations/automation-edit-form-data";
import { EnvironmentClassSelector, RepoFilteringContent } from "@/components/automations/AutomationEditRepoCodeContext";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { Pill } from "@/components/podkit/status/Pill";
import { Text } from "@/components/podkit/typography/Text";
import { ProjectCombobox } from "@/components/projects/ProjectCombobox";
import type { EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { useListProjects, type PlainProject } from "@/queries/project-queries";
import type React from "react";
import { useCallback, type FC } from "react";

type WorkflowEditCodeContextProps = {
    context?: WorkflowEditFormTriggerContext;
    onChange: (context: WorkflowEditFormTriggerContext) => void;
    disabled?: boolean;
};

const CONTEXT_ITEMS = {
    [WorkflowEditFormTriggerContextType.Projects]: {
        name: "Projects",
        description: "Run steps across your Ona projects",
        enabled: true,
    },
    [WorkflowEditFormTriggerContextType.Repositories]: {
        name: "Repositories",
        description: "Target code repositories by filter or search",
        enabled: true,
    },
} as const;

// MARK: entry point

export const WorkflowEditCodeContext: FC<WorkflowEditCodeContextProps> = ({ context, onChange, disabled = false }) => {
    const handleTargetChange = (value: WorkflowEditFormTriggerContextType) => {
        let nextContext: WorkflowEditFormTriggerContext;
        switch (value) {
            case WorkflowEditFormTriggerContextType.Projects:
                nextContext = {
                    type: WorkflowEditFormTriggerContextType.Projects,
                    projectIds: [],
                };
                break;
            case WorkflowEditFormTriggerContextType.Repositories:
                nextContext = {
                    type: WorkflowEditFormTriggerContextType.Repositories,
                    repoSearchString: "",
                    scmHost: "",
                    runnerId: "",
                    environmentClassId: "",
                };
                break;
            default:
                nextContext = {
                    type: WorkflowEditFormTriggerContextType.Projects,
                    projectIds: [],
                };
                break;
        }
        onChange(nextContext);
    };

    const handleProjectSelectionChange = useCallback(
        (projectIds: string[]) => {
            if (context?.type === WorkflowEditFormTriggerContextType.Projects) {
                onChange({
                    ...context,
                    projectIds,
                });
            }
        },
        [context, onChange],
    );

    const handleEnvironmentClassSelectionChange = useCallback(
        (selectedEntry: EnvironmentClassEntry) => {
            if (
                context?.type === WorkflowEditFormTriggerContextType.Repositories &&
                (context.runnerId !== selectedEntry.runner.runnerId ||
                    context.environmentClassId !== selectedEntry.clazz.id)
            ) {
                onChange({
                    ...context,
                    runnerId: selectedEntry.runner.runnerId,
                    environmentClassId: selectedEntry.clazz.id,
                });
            }
        },
        [context, onChange],
    );

    const handleRemoveProject = useCallback(
        (projectId: string) => {
            if (context?.type === WorkflowEditFormTriggerContextType.Projects) {
                onChange({
                    ...context,
                    projectIds: context?.projectIds?.filter((id: string) => id !== projectId) || [],
                });
            }
        },
        [context, onChange],
    );

    const handleAddRepoSearchString = useCallback(
        (filter: string) => {
            if (context?.type === WorkflowEditFormTriggerContextType.Repositories) {
                onChange({ ...context, repoSearchString: filter });
            }
        },
        [context, onChange],
    );

    const handleClearRepoSearchString = useCallback(() => {
        if (context?.type === WorkflowEditFormTriggerContextType.Repositories) {
            onChange({ ...context, repoSearchString: "" });
        }
    }, [context, onChange]);

    const handleScmHostChange = useCallback(
        (scmHost: string) => {
            if (context?.type === WorkflowEditFormTriggerContextType.Repositories) {
                onChange({ ...context, scmHost });
            }
        },
        [context, onChange],
    );
    const handleAddProject = useCallback(
        (projectId: string) => {
            if (
                context?.type === WorkflowEditFormTriggerContextType.Projects &&
                !context.projectIds.includes(projectId)
            ) {
                handleProjectSelectionChange([...context.projectIds, projectId]);
            }
        },
        [context, handleProjectSelectionChange],
    );

    // MARK: render ctx selectors

    let contextSelectors: React.ReactNode;
    switch (context?.type) {
        case WorkflowEditFormTriggerContextType.Projects:
            contextSelectors = (
                <ProjectCombobox
                    selectedProject={null}
                    onProjectChange={handleAddProject}
                    excludeProjectIds={context.projectIds}
                    disabled={disabled}
                    placeholder="Add a project…"
                    className="w-fit"
                />
            );
            break;
        case WorkflowEditFormTriggerContextType.Repositories:
            contextSelectors = (
                <EnvironmentClassSelector
                    selectedEnvironmentClassId={context.environmentClassId}
                    disabled={disabled}
                    onSelectionChange={handleEnvironmentClassSelectionChange}
                />
            );
            break;
    }

    // displays the selected context, eg selected projects or repository search string
    let selectedContextContent: React.ReactNode;
    switch (context?.type) {
        case WorkflowEditFormTriggerContextType.Projects:
            selectedContextContent = (
                <SelectedProjectsContent
                    selectedProjectIds={context.projectIds}
                    disabled={disabled}
                    onRemoveProject={handleRemoveProject}
                />
            );
            break;
        case WorkflowEditFormTriggerContextType.Repositories:
            if (context?.runnerId) {
                selectedContextContent = (
                    <RepoFilteringContent
                        disabled={disabled}
                        runnerId={context?.runnerId}
                        repoSearchString={context?.repoSearchString}
                        scmHost={context?.scmHost}
                        onScmHostChange={handleScmHostChange}
                        onAddFilter={handleAddRepoSearchString}
                        onClearFilter={handleClearRepoSearchString}
                    />
                );
            } else {
                selectedContextContent = null;
            }
            break;
        default:
            selectedContextContent = null;
            break;
    }

    return (
        <Card variant="bordered" className="flex flex-col gap-4 bg-transparent px-3.5 pb-3.5 pt-2">
            <div className="flex flex-row flex-wrap items-center gap-2">
                <Text className="text-base font-medium text-content-primary">Runs for</Text>
                <Select value={context?.type} onValueChange={handleTargetChange} disabled={disabled}>
                    <SelectTrigger className="w-min px-3" loading={false}>
                        <SelectValue>
                            {context ? CONTEXT_ITEMS[context.type].name : "Select a context type"}
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                        {Object.entries(CONTEXT_ITEMS).map(([value, item]) => (
                            <SelectItem key={value} value={value} disabled={!item.enabled}>
                                <div className="flex flex-col gap-1">
                                    <Text className="text-base font-bold">{item.name}</Text>
                                    <Text className="text-content-secondary">{item.description}</Text>
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {contextSelectors}
            </div>
            {selectedContextContent}
        </Card>
    );
};

// MARK: project code context

const SelectedProjectsContent: FC<{
    selectedProjectIds: string[];
    disabled: boolean;
    onRemoveProject: (projectId: string) => void;
}> = ({ selectedProjectIds, disabled, onRemoveProject }) => {
    const { data: projectsData, isLoading } = useListProjects({
        enabled: selectedProjectIds.length > 0,
        projectIds: selectedProjectIds,
    });

    if (selectedProjectIds.length === 0 || (projectsData?.projects.length === 0 && !isLoading)) {
        return (
            <Text className="text-base text-content-muted">
                Add projects to run the automation for each selected project.
            </Text>
        );
    }

    return (
        <div className="flex flex-row flex-wrap gap-2">
            {projectsData?.projects.map((project: PlainProject) => (
                <Pill key={project.id} className="w-fit" variant="neutral" size="lg">
                    <div className="flex items-center gap-1">
                        <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            disabled={disabled}
                            onClick={() => onRemoveProject(project.id)}
                            LeadingIcon={(_size) => <IconClose size="sm" className="size-3" />}
                            className="size-4"
                            data-tracking-id="remove-project-trigger-project-selector"
                        />
                        <span className="truncate text-sm font-bold">{project.metadata?.name}</span>
                    </div>
                </Pill>
            ))}
        </div>
    );
};
